import {
    IonBackButton,
    IonButton,
    IonButtons,
    IonCard,
    IonCardContent,
    IonContent,
    IonHeader,
    IonIcon,
    IonPage,
    IonTitle,
    IonToolbar,
    IonItem,
    IonLabel,
    IonInput,
    IonTextarea,
    IonToggle,
    IonSelect,
    IonSelectOption,
    IonRange,
    IonList,
    IonListHeader,
    IonNote,
    IonRow,
    IonCol,
    IonToast,
    useIonRouter
} from '@ionic/react';
import {
    settingsOutline,
    saveOutline,
    refreshOutline,
    shieldCheckmarkOutline,
    notificationsOutline,
    timeOutline,
    peopleOutline,
    barChartOutline,
    cloudUploadOutline,
    lockClosedOutline,
    mailOutline,
    globeOutline,
    warningOutline,
    gridOutline
} from 'ionicons/icons';
import { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../config/api';
import './View.css';
import Loader from '../components/Loader';

const AdminSettings = () => {
    const router = useIonRouter();
    const [loading, setLoading] = useState(false);

    const [settings, setSettings] = useState({
        // General Settings
        siteName: 'PeopPoll',
        siteDescription: 'A modern platform for creating and participating in opinion polls',
        siteUrl: 'https://peoppoll.com',
        maintenanceMode: false,
        registrationEnabled: true
    });

    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', color: 'success' });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        const safetyTimeout = setTimeout(() => {
            setLoading(false);
        }, 12000);

        try {
            const response = await axios.get(API_CONFIG.MANAGE_SETTINGS, { timeout: 8000 });
            if (response.data.success) {
                const s = response.data.settings;
                setSettings({
                    siteName: s.site_name || 'PeopPoll',
                    siteDescription: s.site_description || 'A modern platform for creating and participating in opinion polls',
                    siteUrl: s.site_url || 'https://peoppoll.com',
                    maintenanceMode: !!s.maintenance_mode,
                    registrationEnabled: s.registration_enabled !== false // Default true
                });
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            setToast({ show: true, message: 'Failed to fetch settings', color: 'danger' });
        } finally {
            clearTimeout(safetyTimeout);
            setLoading(false);
        }
    };

    const handleSettingChange = (key, value) => {
        setSettings(prev => ({
            ...prev,
            [key]: value
        }));
        setUnsavedChanges(true);
    };

    const saveSettings = async () => {
        setSaving(true);
        try {
            const payload = {
                settings: {
                    site_name: settings.siteName,
                    site_description: settings.siteDescription,
                    site_url: settings.siteUrl,
                    maintenance_mode: settings.maintenanceMode,
                    registration_enabled: settings.registrationEnabled
                }
            };
            
            const response = await axios.post(API_CONFIG.MANAGE_SETTINGS, payload);
            if (response.data.success) {
                setUnsavedChanges(false);
                setToast({ show: true, message: 'Settings saved successfully', color: 'success' });
            } else {
                setToast({ show: true, message: response.data.error || 'Failed to save', color: 'danger' });
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            setToast({ show: true, message: 'Connection error while saving', color: 'danger' });
        } finally {
            setSaving(false);
        }
    };

    const resetToDefaults = () => {
        setSettings({
            siteName: 'PeopPoll',
            siteDescription: 'A modern platform for creating and participating in opinion polls',
            siteUrl: 'https://peoppoll.com',
            maintenanceMode: false,
            registrationEnabled: true
        });
        setUnsavedChanges(true);
    };

    return (
        <IonPage className="view-page bg-gray-50 admin-teal-theme">
            <IonHeader className="ion-no-border" translucent={true}>
                <IonToolbar className="header-toolbar frosted-header px-4">
                    <IonButtons slot="start">
                        <IonBackButton text="Back" className="custom-back-button" defaultHref="/admin" />
                    </IonButtons>
                    <IonTitle className="page-title text-center font-bold text-xl uppercase tracking-wider">
                        <span className="title-text" style={{ background: 'none', WebkitTextFillColor: '#14b8a6', color: '#14b8a6' }}>
                            System Settings
                        </span>
                    </IonTitle>
                    <IonButtons slot="end">
                        <IonButton fill="clear" onClick={resetToDefaults} className="text-gray-500">
                            <IonIcon icon={refreshOutline} className="text-xl" />
                        </IonButton>
                        <IonButton
                            fill="solid"
                            onClick={saveSettings}
                            disabled={saving}
                            className="save-btn-settings"
                            style={{
                                '--background': '#444',
                                '--color': 'white',
                                '--border-radius': '6px',
                                '--padding-start': '16px',
                                '--padding-end': '16px',
                                'height': '36px',
                                'fontSize': '14px',
                                'fontWeight': '600'
                            }}
                        >
                            <IonIcon icon={saveOutline} slot="start" className="mr-1" />
                            {saving ? 'SAVING...' : 'SAVE'}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>

            <IonContent fullscreen className="view-content ion-padding">
                {loading && <Loader message="Configuring System Environment..." />}

                <div className="max-w-4xl mx-auto pt-4 relative">
                    {/* General Settings Card */}
                    <IonCard className="settings-main-card shadow-sm border border-gray-100 rounded-2xl overflow-visible">
                        <IonCardContent className="p-8">
                            {/* Card Header Section */}
                            <div className="flex items-center space-x-5 mb-8 pb-6 border-b border-gray-100">
                                <div className="rounded-full p-4 shadow-inner" style={{ backgroundColor: '#14b8a620' }}>
                                    <IonIcon icon={globeOutline} className="text-2xl" style={{ color: '#14b8a6' }} />
                                </div>
                                <div className="flex-1">
                                    <h2 className="text-2xl font-bold text-gray-800 tracking-tight">General Settings</h2>
                                    <p className="text-gray-500 font-medium">Basic site configuration and branding</p>
                                </div>
                            </div>

                            <div className="space-y-8">
                                {/* Site Name */}
                                <div className="settings-field-group">
                                    <label className="block text-sm font-semibold text-gray-500 uppercase tracking-widest mb-2">Site Name</label>
                                    <IonInput
                                        value={settings.siteName}
                                        onIonInput={(e) => handleSettingChange('siteName', e.detail.value)}
                                        className="modern-settings-input"
                                        style={{
                                            '--background': 'transparent',
                                            '--padding-start': '0',
                                            'fontSize': '18px',
                                            'fontWeight': '500',
                                            'color': '#222',
                                            'borderBottom': '1px solid #eee'
                                        }}
                                    />
                                </div>

                                {/* Site Description */}
                                <div className="settings-field-group">
                                    <label className="block text-sm font-semibold text-gray-500 uppercase tracking-widest mb-2">Site Description</label>
                                    <IonTextarea
                                        value={settings.siteDescription}
                                        onIonInput={(e) => handleSettingChange('siteDescription', e.detail.value)}
                                        rows={2}
                                        className="modern-settings-input"
                                        style={{
                                            '--background': 'transparent',
                                            '--padding-start': '0',
                                            'fontSize': '18px',
                                            'fontWeight': '500',
                                            'color': '#222',
                                            'borderBottom': '1px solid #eee'
                                        }}
                                    />
                                </div>

                                {/* Site URL */}
                                <div className="settings-field-group">
                                    <label className="block text-sm font-semibold text-gray-500 uppercase tracking-widest mb-2">Site URL</label>
                                    <IonInput
                                        value={settings.siteUrl}
                                        onIonInput={(e) => handleSettingChange('siteUrl', e.detail.value)}
                                        className="modern-settings-input"
                                        style={{
                                            '--background': 'transparent',
                                            '--padding-start': '0',
                                            'fontSize': '18px',
                                            'fontWeight': '500',
                                            'color': '#222',
                                            'borderBottom': '1px solid #eee'
                                        }}
                                    />
                                </div>

                                {/* Maintenance Mode Switch */}
                                <div className="flex items-center justify-between py-4 border-b border-gray-50 hover:bg-gray-50/50 transition-colors rounded-lg px-2 -mx-2">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-gray-700 mb-1">Maintenance Mode</h3>
                                        <p className="text-gray-400 text-sm">Temporarily disable site access for maintenance</p>
                                    </div>
                                    <IonToggle
                                        checked={settings.maintenanceMode}
                                        onIonChange={(e) => handleSettingChange('maintenanceMode', e.detail.checked)}
                                        className="settings-toggle"
                                        style={{
                                            '--handle-background-checked': '#14b8a6',
                                            '--track-background-checked': 'rgba(20, 184, 166, 0.3)'
                                        }}
                                    />
                                </div>

                                {/* User Registration Switch */}
                                <div className="flex items-center justify-between py-4 hover:bg-gray-50/50 transition-colors rounded-lg px-2 -mx-2">
                                    <div className="flex-1">
                                        <h3 className="text-lg font-semibold text-gray-700 mb-1">User Registration</h3>
                                        <p className="text-gray-400 text-sm">Allow new users to register accounts</p>
                                    </div>
                                    <IonToggle
                                        checked={settings.registrationEnabled}
                                        onIonChange={(e) => handleSettingChange('registrationEnabled', e.detail.checked)}
                                        className="settings-toggle"
                                        style={{
                                            '--handle-background-checked': '#ff9f43',
                                            '--track-background-checked': 'rgba(255, 159, 67, 0.3)'
                                        }}
                                    />
                                </div>
                            </div>
                        </IonCardContent>
                    </IonCard>
                </div>

                <IonToast
                    isOpen={toast.show}
                    onDidDismiss={() => setToast({ ...toast, show: false })}
                    message={toast.message}
                    duration={3000}
                    color={toast.color}
                    position="bottom"
                />
            </IonContent>
        </IonPage>
    );
};

export default AdminSettings;
